import * as ReactDOM from 'react-dom/client';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/capacitor';

import {
	addLocationEffect,
	addMultipleEffects,
	addPushTokenEffect,
	bootstrapBusinessLogic,
	BusinessLayerContext,
	cancelSubscriptionEffect,
	capacityCheckEntity,
	checkBannedEmailEffect,
	deleteCurrentUserEffect,
	DTicketPriceProvider,
	featureFlagEntity,
	feedbacksEntity,
	fetchFeatureFlagsEffect,
	fetchMoplaLegFeedbackEffect,
	fetchSubscriptionOfferingEffect,
	fetchSubscriptionsEffect,
	fetchTicketsEffect,
	fetchUserEffect,
	handle401Effect,
	initDatabase,
	initPassengerPushesEffect,
	inquiryInvoiceEffect,
	launchEntity,
	locationEntity,
	LogoutWrapper,
	passengerBookingEntity,
	passengerBookingReplicationDescriptor,
	pauseSubscriptionEffect,
	removePassengerBookingEffect,
	resumeSubscriptionEffect,
	resyncPassengerBookingEffect,
	revokeCancellationEffect,
	setLaunchEffect,
	setUserLanguageEffect,
	signOutEffect,
	subscriptionEntity,
	ticketEntity,
	userEntity,
	vouchersEntity,
	wipeMoplaFeedbacksData,
	wipeMoplaFeedbacksEffect,
} from '@mopla/business-logic';
import { initI18n } from '@mopla/i18';
import { createLogger, ELogLevel } from '@mopla/logger';
import { PageLoadingIndicator } from '@mopla/ui';
import { shouldEnableDevUtils } from '@mopla/utils';

import './styles.scss';
import './config/dayjs';

import { configureMoplaClient } from './api/configureMoplaClient';
import { configureTicketshopClient } from './api/configureTicketshopClient';
import passApi from './api/passApi';
import { App } from './app/app';
import { initFirebaseApp } from './app/initFirebaseApp';
import { initPassengerSentry } from './app/initPassengerSentry';
import { initUserTracking } from './app/userTracking/initUserTracking';
import { environment } from './environments/environment';

createLogger({
	minLevel: environment.env === 'production' ? ELogLevel.INFO : ELogLevel.DEBUG,
	enableConsole: environment.env !== 'production',
	enableSentry: true,
	sentryMinLevel: ELogLevel.INFO,
	sentryInstance: Sentry,
	environment: environment.env,
	enableRedaction: environment.env === 'production',
	enableAutoBreadcrumbs: environment.env === 'production',
	defaultContext: {
		app: 'passenger',
		platform: Capacitor.getPlatform(),
	},
});

configureMoplaClient(environment.baseUrl);
configureTicketshopClient(environment.ticketshopBaseUrl);

// Initialize i18n with path prefix
initI18n({
	pathPrefix: environment.basename ? '' : '/',
});

const root = ReactDOM.createRoot(
	document.getElementById('root') as HTMLElement
);

(async () => {
	await initPassengerSentry();

	initFirebaseApp();
	initUserTracking();

	/** Check if RxDB dev utilities should be enabled based on environment */
	const shouldAddRxDBDebugUtils = shouldEnableDevUtils(environment.env);

	const db = await initDatabase(shouldAddRxDBDebugUtils);

	const bl = await bootstrapBusinessLogic(
		passApi,
		[
			locationEntity,
			launchEntity,
			userEntity,
			passengerBookingEntity,
			ticketEntity,
			subscriptionEntity,
			vouchersEntity,
			feedbacksEntity,
			capacityCheckEntity,
			featureFlagEntity,
		],
		[passengerBookingReplicationDescriptor(passApi)],
		db,
		shouldAddRxDBDebugUtils
	);

	if (shouldAddRxDBDebugUtils) {
		/** For debug purposes */
		(window as any).bl = bl;
	}

	addMultipleEffects([
		addLocationEffect,
		setLaunchEffect,
		fetchFeatureFlagsEffect,
		fetchTicketsEffect,
		fetchSubscriptionsEffect,
		fetchSubscriptionOfferingEffect,
		fetchUserEffect,
		initPassengerPushesEffect,
		addPushTokenEffect,
		setUserLanguageEffect,
		inquiryInvoiceEffect,
		pauseSubscriptionEffect,
		resumeSubscriptionEffect,
		cancelSubscriptionEffect,
		revokeCancellationEffect,
		checkBannedEmailEffect,
		handle401Effect,
		signOutEffect,
		deleteCurrentUserEffect,
		removePassengerBookingEffect,
		fetchMoplaLegFeedbackEffect,
		wipeMoplaFeedbacksEffect,
		resyncPassengerBookingEffect,
	]);

	bl.dispatch(wipeMoplaFeedbacksData());

	root.render(
		<DTicketPriceProvider config={environment.deutschlandTicket}>
			<BusinessLayerContext.Provider value={bl}>
				<LogoutWrapper logoutEl={<PageLoadingIndicator />}>
					<App />
				</LogoutWrapper>
			</BusinessLayerContext.Provider>
		</DTicketPriceProvider>
	);
})();
